/**
 * WebhooksSettings
 * 
 * Página de configuração de webhooks.
 * Container principal que gerencia o toolbar e a troca de abas.
 * 
 * Refatorado: Conteúdo extraído para OutgoingWebhooksTab e IncomingEndpointsTab.
 */

import React from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Card } from '../../../components/ui/Card';
import SearchInput from '../../../components/ui/SearchInput';
import Spinner from '../../../components/ui/Spinner';
import { useWebhooksPage } from './hooks/useWebhooksPage';
import ActionMenu, { createImportAction, createExportAction } from '../../../components/shared/ActionMenu';
import { notify } from '../../../utils/toast';
import { Button } from '../../../components/ui/Button';
import { PlusIcon } from '../../../components/icons';
import { AppIcon } from '../../../components/icons/AppIcon';
import OutgoingWebhooksTab from './components/OutgoingWebhooksTab';
import IncomingEndpointsTab from './components/IncomingEndpointsTab';

const WebhooksSettings: React.FC = () => {
    const page = useWebhooksPage();

    const headerActions = [
        createImportAction(() => notify.info('Importação em desenvolvimento')),
        createExportAction(() => notify.info('Exportação em desenvolvimento')),
    ];

    return (
        <div className="h-full flex flex-col">
            <PageHeader
                title="Webhooks"
                subtitle="Integre o Adlitics com outros sistemas recebendo ou enviando dados."
                hideBackButton
                action={
                    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2.5">
                        {/* Mobile Top: Tabs + Actions + Refresh */}
                        <div className="flex justify-between items-center md:hidden">
                            {/* Abas - só ícones */}
                            <div className="flex bg-surface-subtle rounded-lg p-1 border border-border">
                                <button
                                    onClick={() => page.setActiveTab('incoming')}
                                    className={`p-2 rounded-md transition-colors ${page.activeTab === 'incoming' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-default'}`}
                                    title="Receber webhooks (Incoming)"
                                >
                                    <AppIcon name="download" className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => page.setActiveTab('outgoing')}
                                    className={`p-2 rounded-md transition-colors ${page.activeTab === 'outgoing' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-default'}`}
                                    title="Enviar webhooks (Outgoing)"
                                >
                                    <AppIcon name="upload" className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                <ActionMenu actions={headerActions} />
                            </div>
                        </div>

                        {/* Search */}
                        <div className="w-full md:w-[250px] flex-shrink-0">
                            <SearchInput
                                value={page.searchTerm}
                                onChange={page.setSearchTerm}
                                placeholder={page.activeTab === 'outgoing' ? 'Pesquisar webhooks...' : 'Pesquisar endpoints...'}
                            />
                        </div>

                        {/* Botão Novo */}
                        <Button
                            onClick={page.activeTab === 'outgoing' ? page.openCreateWebhook : page.openCreateEndpoint}
                            className="w-full md:w-auto"
                        >
                            Novo {page.activeTab === 'outgoing' ? 'Webhook' : 'Endpoint'}
                        </Button>

                        {/* Desktop Tabs & Actions */}
                        <div className="hidden md:flex items-center gap-2.5">
                            {/* Abas - só ícones */}
                            <div className="flex bg-surface-subtle rounded-lg p-1 border border-border">
                                <button
                                    onClick={() => page.setActiveTab('incoming')}
                                    className={`p-2 rounded-md transition-colors ${page.activeTab === 'incoming' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-default'}`}
                                    title="Receber webhooks (Incoming)"
                                >
                                    <AppIcon name="download" className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => page.setActiveTab('outgoing')}
                                    className={`p-2 rounded-md transition-colors ${page.activeTab === 'outgoing' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-default'}`}
                                    title="Enviar webhooks (Outgoing)"
                                >
                                    <AppIcon name="upload" className="h-4 w-4" />
                                </button>
                            </div>

                            <ActionMenu actions={headerActions} />
                        </div>
                    </div>
                }
            />

            {/* Tabela e Conteúdo */}
            <div className="overflow-x-auto relative mt-4">
                {/* Loading overlay */}
                {page.loading && (
                    <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                        <Spinner size="lg" />
                    </div>
                )}

                {page.activeTab === 'outgoing' ? (
                    <OutgoingWebhooksTab page={page} />
                ) : (
                    <IncomingEndpointsTab page={page} />
                )}
            </div>
        </div>
    );
};

export default WebhooksSettings;
